import React, { useState, useEffect } from 'react';
import { Text } from '@uhg-abyss/web/ui/Text';
import { IconMaterial } from '@uhg-abyss/web/ui/IconMaterial';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Button } from '@uhg-abyss/web/ui/Button';
import { styled } from '@uhg-abyss/web/tools/styled';
import { Divider } from '@uhg-abyss/web/ui/Divider';
import { usePropsContext } from '@uhg-abyss/web/hooks/usePropsContext';
import { LoadingOverlay as Loading } from '@uhg-abyss/web/ui/LoadingOverlay';
import { usercache } from '../../../../redux/tools/usercache';
import { useOrganizationsQuery } from '../../../../common/hooks/useOrganizationsQuery';
import { useProvidersQuery } from '../../../../common/hooks/useProvidersQuery';
import { useSaveProviderPreferenceQuery } from '../../../../common/hooks/useSaveProviderPreferenceQuery';
import { Corporates } from './Corporates';
import { TaxIdNumbers } from './TaxIdNumbers';
import { CareProviders } from './CareProviders';
import { ProviderConfirmationModal } from './ProviderConfirmationModal';

const FullWidthButton = styled(Button, {
  width: '100%',
});

const MarginDiv = styled('div', {
  margin: '10px 0px 15px',
});

const Container = styled('div', {
  height: 'fit-content',
  width: '500px',
  padding: '10px',
});

export const ProviderSelect = () => {
  const { data, events } = usePropsContext();
  const { user, provider, confirmSelection, isDropdownHidden } = data;
  const { uuid, isInternalUser, isIntakeUser, corporates } = user;
  const [, setProvidersErrorMessage] = useState('');
  const [allowProviderSearch, setAllowProviderSearch] = useState(
    !(isIntakeUser || isInternalUser)
  );

  const [defaultProvider, setDefaultProvider] = useState({}); // only to be updated on successful save of new provider
  const [selectedProvider, setSelectedProvider] = useState({});
  const [confirmProvider, setConfirmProvider] = useState();
  const [organizations, setOrganizations] = useState([]);
  const [careProviders, setCareProviders] = useState([]);
  const [canCallProviders, setCanCallProviders] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [organizationsQueryResult, getOrganizationsQuery] =
    useOrganizationsQuery({
      onCompleted: (response) => {
        if (
          response.organizations &&
          response.organizations?.permissions?.length > 0
        ) {
          setOrganizations(response.organizations.permissions);
          setCanCallProviders(true);
        }
      },
      onError: (err) => {
        setErrorModalOpen(true);
        events.onError(err);
      },
    });
  const [careProvidersQueryResult, getCareProvidersQuery] = useProvidersQuery({
    onCompleted: (response) => {
      if (response.providers && response.providers?.length > 0) {
        setCareProviders(response.providers);
      }
      setCanCallProviders(false);
    },
    onError: (err) => {
      setErrorModalOpen(true);

      events.onError(err);
      const { code, message } = err;

      setCareProviders([]);
      events.onDigitalSecurityError({
        code,
        message,
        isDigitalSecurityError: true,
      });
    },
  });
  const [saveProviderPreferenceResult, saveProviderPreference] =
    useSaveProviderPreferenceQuery({
      onCompleted: (response) => {
        const { provider: providerResponse } = response;
        const foundOrg = organizations.find(
          (org) => org.tin === providerResponse.taxIdNumber
        );

        const responseFormatted = {
          ...response,
          provider: {
            ...providerResponse,
            permissionCode: foundOrg.permissionCode || [],
            permissionSubCode: foundOrg.permissionSubCode || [],
            privileges: foundOrg.privileges || [],
            allPermission: foundOrg.allPermission || false,
          },
          permissionCode: foundOrg.permissionCode || [],
          permissionSubCode: foundOrg.permissionSubCode || [],
          privileges: foundOrg.privileges || [],
          allPermission: foundOrg.allPermission || false,
        };

        if (!data.saveDisabled) {
          usercache.set('provider', provider);
        }

        events.onChange(responseFormatted);
        events.onSave(providerResponse.servicingProviderMpin);
        setDefaultProvider(providerResponse);
      },
      onError: (err) => {
        setErrorModalOpen(true);
        events.onError(err);
      },
    });

  const fetchOrganizations = (providerObj) => {
    const { corporateProviderMpin, uhcProviderId, userId, uniqueId } =
      providerObj;

    getOrganizationsQuery({
      variables: {
        uuid,
        corporateProviderMpin,
        uhcProviderId,
        userId,
        uniqueId,
      },
    });
  };

  const fetchCareProviders = (providerObj) => {
    const {
      corporateProviderMpin,
      taxIdNumber,
      uhcProviderId,
      uniqueId,
      userId,
    } = providerObj;

    getCareProvidersQuery({
      variables: {
        corporateProviderMpin,
        taxIdNumber,
        uhcProviderId,
        uniqueId,
        userId,
        uuid,
      },
    });
  };

  useEffect(() => {
    if (provider) {
      setSelectedProvider(provider);
      setDefaultProvider(provider);
      if (
        provider.corporateProviderMpin &&
        provider.userId &&
        provider.uhcProviderId
      ) {
        fetchOrganizations(provider);
      }
    }
  }, []);

  useEffect(() => {
    if (canCallProviders) {
      let providerSearchObj = selectedProvider;
      if (selectedProvider.uniqueId !== defaultProvider.uniqueId) {
        const firstTin = organizations[0];
        providerSearchObj = {
          ...providerSearchObj,
          organizationId: firstTin.organizationId || '',
          organizationName: firstTin.name || '',
          taxIdNumber: firstTin.tin,
          tinType: firstTin.tinType,
          permissionCode: firstTin.permissionCode || [],
          permissionSubCode: firstTin.permissionSubCode || [],
          privileges: firstTin.privileges || [],
          allPermission: firstTin.allPermission || false,
        };
        setSelectedProvider(providerSearchObj);
      }
      fetchCareProviders(providerSearchObj);
    }
  }, [canCallProviders]);

  const handleSave = () => {
    saveProviderPreference({
      variables: {
        organization: {
          id: selectedProvider.organizationId,
          name: selectedProvider.organizationName,
          permissions:
            selectedProvider.privileges || selectedProvider.permissions,
          allPermission: selectedProvider.allPermission,
        },
        corporateProvider: {
          mpin: selectedProvider.corporateProviderMpin,
          name: selectedProvider.corporateProviderName,
          uniqueId: selectedProvider.uniqueId,
        },
        taxIdNumber: {
          tin: selectedProvider.taxIdNumber,
          tinType: selectedProvider.tinType,
        },
        servicingProvider: {
          mpin: selectedProvider.servicingProviderMpin,
          name: selectedProvider.servicingProviderName,
        },
        uuid,
        uhcProviderId: selectedProvider.uhcProviderId,
        userId: selectedProvider.userId,
        save: !data.saveDisabled, // this is always true since data.saveDisabled is never defined
      },
    });
  };

  const handleSelection = () => {
    if (confirmSelection) {
      setConfirmProvider(selectedProvider);
    } else {
      handleSave();
    }
  };

  const handleReset = () => {
    if (defaultProvider?.organization || defaultProvider?.uniqueId) {
      setSelectedProvider(defaultProvider);
    } else {
      setSelectedProvider({});
    }
  };

  const handleCancel = () => {
    handleReset();
    events.onCancel();
  };

  const handleNewCorporateSelection = (newCorporateSelection) => {
    fetchOrganizations(newCorporateSelection);
  };

  // ===== PROPS FOR SELECTINPUTS ====
  const corporatesProps = {
    corporates,
    selectedProvider,
    setSelectedProvider,
    setAllowProviderSearch,
    handleNewCorporateSelection,
  };

  const taxIdAndCareProvidersProps = {
    isDropdownHidden,
    uuid,
    selectedProvider,
    setSelectedProvider,
    allowProviderSearch,
    organizations,
    setProvidersErrorMessage,
    careProviders,
    isTaxIdNumbersLoading: organizationsQueryResult.loading,
    isCareProvidersLoading:
      organizationsQueryResult.loading || careProvidersQueryResult.loading,
  };

  return (
    <div>
      <Loading
        label='Care Provider'
        loadingTitle='Please Wait'
        loadingMessage='Retrieving provider information'
        isDismissable
        statusIcon='warning'
        isLoading={
          organizationsQueryResult.loading ||
          data.application ||
          saveProviderPreferenceResult.loading ||
          careProvidersQueryResult.loading
        }
        isOpen={errorModalOpen}
        statusMessage={
          saveProviderPreferenceResult.error?.message ||
          organizationsQueryResult.error?.message ||
          careProvidersQueryResult.error?.message
        }
        onClose={() => setErrorModalOpen(false)}
      >
        <Text size='20px' fontWeight='bold' color='$primary1'>
          {data.title}
        </Text>
        <Divider />
        <Container>
          <Layout.Group>
            <IconMaterial icon='info' />
            <Text>Begin typing in the fields below to search.</Text>
          </Layout.Group>
          <MarginDiv>
            <Corporates {...corporatesProps} />
            <TaxIdNumbers {...taxIdAndCareProvidersProps} />
            <CareProviders {...taxIdAndCareProvidersProps} />
          </MarginDiv>
          <Layout.Group grow alignLayout='center'>
            <FullWidthButton
              id='provider-select-cancel-button'
              data-testid='provider-select-cancel-button'
              variant='outline'
              onClick={() => handleCancel()}
            >
              Cancel
            </FullWidthButton>
            <FullWidthButton
              id='provider-select-save-button'
              data-testid='provider-select-save-button'
              isDisabled={!allowProviderSearch}
              onClick={() => handleSelection()}
            >
              Save
            </FullWidthButton>
          </Layout.Group>
        </Container>
      </Loading>
      <ProviderConfirmationModal
        isOpen={!!confirmProvider}
        onCancel={() => setConfirmProvider(null)}
        onContinue={() => handleSave()}
      />
    </div>
  );
};
